import { useState } from 'react';
import "./BootcampsPage.css"
import { Calendar, Users, Code, Zap, Rocket, BookOpen, Award, Target, Clock, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import qiskit from '../../assets/qiskit.jpg';
import classiq from '../../assets/classiq.jpg';
import cirq from '../../assets/cirq.jpg';
import pennylane from '../../assets/pennylane.jpg';
import forest from '../../assets/forest.jpg';
import bracket from '../../assets/bracket.jpg';

const BootcampsPage = () => {
  const [currentCard, setCurrentCard] = useState(0);

  const handlePrev = () => {
    setCurrentCard((prev) => (prev === 0 ? upcomingBootcamps.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentCard((prev) => (prev === upcomingBootcamps.length - 1 ? 0 : prev + 1));
  };

  const frameworks = [
    {
      name: "Qiskit",
      company: "IBM",
      logo: qiskit,
      colorClass: "bootcamps-framework-badge-blue",
    },
    {
      name: "Classiq",
      company: "Classiq",
      logo: classiq,
      colorClass: "bootcamps-framework-badge-purple",
    },
    {
      name: "Cirq",
      company: "Google",
      logo: cirq,
      colorClass: "bootcamps-framework-badge-green",
    },
    {
      name: "PennyLane",
      company: "Xanadu",
      logo: pennylane,
      colorClass: "bootcamps-framework-badge-orange",
    },
    {
      name: "Forest",
      company: "Rigetti",
      logo: forest,
      colorClass: "bootcamps-framework-badge-red",
    },
    {
      name: "Braket",
      company: "AWS",
      logo: bracket,
      colorClass: "bootcamps-framework-badge-yellow",
    },
  ]

  const upcomingBootcamps = [
    {
      title: "Quantum Machine Learning Intensive",
      framework: "Qiskit + PennyLane",
      date: "March 15-17, 2024",
      duration: "3 Days",
      level: "Intermediate",
      participants: 45,
      maxParticipants: 60,
      location: "Hybrid (Nairobi + Online)",
      price: "Free",
      description: "Dive into quantum machine learning algorithms and practical implementations.",
    },
    {
      title: "Quantum Computing Fundamentals",
      framework: "Classiq",
      date: "April 8-12, 2024",
      duration: "5 Days",
      level: "Beginner",
      participants: 78,
      maxParticipants: 100,
      location: "Online",
      price: "Free",
      description: "Perfect introduction to quantum computing concepts and hands-on programming.",
    },
    {
      title: "Advanced Quantum Algorithms Bootcamp",
      framework: "Cirq + Classiq",
      date: "September 20-24, 2024",
      duration: "5 Days",
      level: "Advanced",
      participants: 23,
      maxParticipants: 40,
      location: "iHub Nairobi",
      price: "KES 5,000",
      description: "Explore cutting-edge quantum algorithms and optimization techniques.",
    },
  ]

  return (
    <div className="bootcamps-page">
      {/* Hero Section */}
      <section className="bootcamps-hero-section">
        <div className="bootcamps-hero-background"></div>
        <div className="bootcamps-container">
          <div className="bootcamps-hero-content">
            <div className="bootcamps-hero-text">
              <div className="bootcamps-hero-badge">
                <Zap className="bootcamps-icon" />
                Quantum Computing Bootcamps
              </div>
              <h1 className="bootcamps-hero-title">
                Master the
                <span className="bootcamps-gradient-text"> Quantum </span>
                Future
              </h1>
              <p className="bootcamps-hero-description">
                Join Africa's premier quantum computing community. Learn cutting-edge quantum frameworks, build
                real-world projects, and connect with quantum pioneers across the continent.
              </p>

              <div className="bootcamps-hero-buttons">
                <button className="bootcamps-btn bootcamps-btn-primary">
                  <Rocket className="bootcamps-icon" />
                  Join Next Bootcamp
                </button>
                <button className="bootcamps-btn bootcamps-btn-outline">
                  <Users className="bootcamps-icon" />
                  Organize Bootcamp
                </button>
              </div>

              <div className="bootcamps-hero-stats">
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">200+</div>
                  <div className="bootcamps-stat-label">Graduates</div>
                </div>
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">15+</div>
                  <div className="bootcamps-stat-label">Bootcamps</div>
                </div>
                <div className="bootcamps-stat-item">
                  <div className="bootcamps-stat-value">10+</div>
                  <div className="bootcamps-stat-label">Partners</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quantum Frameworks Section */}
      <section className="bootcamps-frameworks-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">Quantum Frameworks We Master</h2>
            <p className="bootcamps-section-description">
              Learn from industry-leading quantum computing frameworks and tools used by top tech companies worldwide.
            </p>
          </div>

          <div className="bootcamps-frameworks-grid">
            {frameworks.map((framework, index) => (
              <div key={index} className="bootcamps-framework-card">
                <div className="bootcamps-framework-content">
                  <img
                    src={framework.logo || "/placeholder.svg"}
                    alt={`${framework.name} logo`}
                    className="bootcamps-framework-logo"
                  />
                  <h3 className="bootcamps-framework-name">{framework.name}</h3>
                  <span className={`bootcamps-framework-badge ${framework.colorClass}`}>{framework.company}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Bootcamps Section */}
      <section className="bootcamps-upcoming-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">Past Bootcamps</h2>
            <p className="bootcamps-section-description">Join our next quantum computing adventures</p>
          </div>

          {/* Slider container for small screens */}
          <div className="bootcamps-slider-wrapper">
            <button className="bootcamps-slider-arrow left" onClick={handlePrev}>
              <ChevronLeft />
            </button>

            <div className="bootcamps-slider">
              {upcomingBootcamps.map((bootcamp, index) => (
                <div
                  key={index}
                  className={`bootcamps-card ${index === currentCard ? 'active' : 'inactive'}`}
                >
                  <div className="bootcamps-card-header">
                    <div className="bootcamps-card-badges">
                      <span className="bootcamps-level-badge">{bootcamp.level}</span>
                      <span className="bootcamps-price-badge">{bootcamp.price}</span>
                    </div>
                    <h3 className="bootcamps-card-title">{bootcamp.title}</h3>
                    <p className="bootcamps-card-description">{bootcamp.description}</p>
                  </div>
                  <div className="bootcamps-card-content">
                    <div className="bootcamps-card-details">
                      <div className="bootcamps-detail-item">
                        <Code className="bootcamps-detail-icon" />
                        {bootcamp.framework}
                      </div>
                      <div className="bootcamps-detail-item">
                        <Calendar className="bootcamps-detail-icon" />
                        {bootcamp.date}
                      </div>
                      <div className="bootcamps-detail-item">
                        <Clock className="bootcamps-detail-icon" />
                        {bootcamp.duration}
                      </div>
                      <div className="bootcamps-detail-item">
                        <MapPin className="bootcamps-detail-icon" />
                        {bootcamp.location}
                      </div>
                    </div>

                    <div className="bootcamps-participants-section">
                      <div className="bootcamps-participants-header">
                        <span>Participants</span>
                        <span>
                          {bootcamp.participants}/{bootcamp.maxParticipants}
                        </span>
                      </div>
                      <div className="bootcamps-progress-bar-bg">
                        <div
                          className="bootcamps-progress-bar"
                          style={{ width: `${(bootcamp.participants / bootcamp.maxParticipants) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button className="bootcamps-slider-arrow right" onClick={handleNext}>
              <ChevronRight />
            </button>
          </div>
          
         {/* Fallback grid for larger screens */}
<div className="bootcamps-grid">
  {upcomingBootcamps.map((bootcamp, index) => (
    <div key={index} className="bootcamps-card">
      <div className="bootcamps-card-header">
        <div className="bootcamps-card-badges">
          <span className="bootcamps-level-badge">{bootcamp.level}</span>
          <span className="bootcamps-price-badge">{bootcamp.price}</span>
        </div>
        <h3 className="bootcamps-card-title">{bootcamp.title}</h3>
        <p className="bootcamps-card-description">{bootcamp.description}</p>
      </div>
      <div className="bootcamps-card-content">
        <div className="bootcamps-card-details">
          <div className="bootcamps-detail-item">
            <Code className="bootcamps-detail-icon" />
            {bootcamp.framework}
          </div>
          <div className="bootcamps-detail-item">
            <Calendar className="bootcamps-detail-icon" />
            {bootcamp.date}
          </div>
          <div className="bootcamps-detail-item">
            <Clock className="bootcamps-detail-icon" />
            {bootcamp.duration}
          </div>
          <div className="bootcamps-detail-item">
            <MapPin className="bootcamps-detail-icon" />
            {bootcamp.location}
          </div>
        </div>

        <div className="bootcamps-participants-section">
          <div className="bootcamps-participants-header">
            <span>Participants</span>
            <span>
              {bootcamp.participants}/{bootcamp.maxParticipants}
            </span>
          </div>
          <div className="bootcamps-progress-bar-bg">
            <div
              className="bootcamps-progress-bar"
              style={{ width: `${(bootcamp.participants / bootcamp.maxParticipants) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  ))}
</div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bootcamps-how-it-works-section">
        <div className="bootcamps-container">
          <div className="bootcamps-section-header">
            <h2 className="bootcamps-section-title">How Our Bootcamps Work</h2>
            <p className="bootcamps-section-description">A structured approach to quantum mastery</p>
          </div>

          <div className="bootcamps-steps-grid">
            <div className="bootcamps-step-item">
              <div className="bootcamps-step-icon-container bootcamps-step-blue">
                <BookOpen className="bootcamps-step-icon" />
              </div>
              <h3 className="bootcamps-step-title">Learn Fundamentals</h3>
              <p className="bootcamps-step-description">Master quantum computing theory and mathematical foundations</p>
            </div>

            <div className="bootcamps-step-item">
              <div className="bootcamps-step-icon-container bootcamps-step-purple">
                <Code className="bootcamps-step-icon" />
              </div>
              <h3 className="bootcamps-step-title">Hands-on Coding</h3>
              <p className="bootcamps-step-description">Build quantum circuits and algorithms using industry frameworks</p>
            </div>

            <div className="bootcamps-step-item">
              <div className="bootcamps-step-icon-container bootcamps-step-green">
                <Target className="bootcamps-step-icon" />
              </div>
              <h3 className="bootcamps-step-title">Real Projects</h3>
              <p className="bootcamps-step-description">Apply knowledge to solve real-world quantum computing challenges</p>
            </div>

            <div className="bootcamps-step-item">
              <div className="bootcamps-step-icon-container bootcamps-step-orange">
                <Award className="bootcamps-step-icon" />
              </div>
              <h3 className="bootcamps-step-title">Get Certified</h3>
              <p className="bootcamps-step-description">Earn industry-recognized certificates and join our alumni network</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default BootcampsPage